import { type ReactElement } from 'react';
import { type FieldPath, type PathValue } from 'react-hook-form';
import { LocalizedCountry } from 'ui/LocalizedCountry/LocalizedCountry';

import {
  type SubmitUnwantedFoodType,
  type UnwantedFoodType,
  type CharityVettingFormValues,
  type CharityVettingUrlTypeValues,
  type CountryCode,
  CountryCodeEnum,
  type LimitedCountryListItem,
} from '../types/charityVettingTypes';

import type { PlaceDetails } from 'api/google-api/types';

// A type-safe form setter over CharityVettingFormValues: the value is tied to
// the field path, so callers need no casts. Matches the generic `setFieldValue`
// helpers the step components pass in.
export type SetCharityFieldValue = <N extends FieldPath<CharityVettingFormValues>>(
  name: N,
  value: PathValue<CharityVettingFormValues, N>,
) => void;

export type StepName = CharityVettingUrlTypeValues;

export type Step = {
  step: StepName;
  reviewComponent: ReactElement;
  label: ReactElement;
};

export const unwantedFoodItemsToCategories = (
  unwantedFoodItems: UnwantedFoodType[],
): SubmitUnwantedFoodType[] =>
  unwantedFoodItems.map((food) => {
    return {
      label: food.code,
      active: !!food.active,
    };
  });

type PlacePrediction = google.maps.places.PlacePrediction;

const getCharityRelevantName = (placeDetails?: PlaceDetails) => {
  const { types = [], name } = placeDetails ?? {};
  const isAnEstablishment = types?.some(
    (type) => type === 'establishment' || type === 'point_of_interest',
  );
  return isAnEstablishment ? name : '';
};

export const setFormPlaceDetails = ({
  suggestion,
  placeDetails,
  setFieldValue,
}: {
  suggestion: PlacePrediction;
  placeDetails?: PlaceDetails;
  setFieldValue: SetCharityFieldValue;
}) => {
  const { address, googlePhoneNumber } = placeDetails ?? {};
  const { displayAddress, addressLine, city, postalCode, addressPosition } = address ?? {};
  const displayAddressRes =
    addressLine?.trim() || displayAddress?.trim() || suggestion?.secondaryText?.text;

  const trimmedGooglePhoneNumber = googlePhoneNumber?.replace(/\s+/g, '');
  const charityName = getCharityRelevantName(placeDetails);

  setFieldValue('charityOnboarding.charityName', charityName ?? '');
  setFieldValue('charityOnboarding.address', displayAddressRes ?? '');
  setFieldValue('charityOnboarding.postalCode', postalCode ?? '');
  setFieldValue('charityOnboarding.city', city ?? '');
  if (trimmedGooglePhoneNumber) {
    setFieldValue('charityOnboarding.phoneNumber', trimmedGooglePhoneNumber);
  }
  setFieldValue('charityOnboarding.gmAddressCoordinates', addressPosition);
};

export const getCountryDisplayText = (
  limitedCountryList: LimitedCountryListItem[],
  countryIsoCode: CountryCode | null,
): LimitedCountryListItem | null => {
  if (!countryIsoCode) {
    return null;
  }
  const found = limitedCountryList.find((country) => country.value === countryIsoCode);
  return found ?? null;
};

export const countryList: LimitedCountryListItem[] = [
  {
    value: CountryCodeEnum.GB,
    flag: '🇬🇧',
    displayText: <LocalizedCountry countryIsoCode={CountryCodeEnum.GB} />,
  },
  {
    value: CountryCodeEnum.PT,
    flag: '🇵🇹',
    displayText: <LocalizedCountry countryIsoCode={CountryCodeEnum.PT} />,
  },
  {
    value: CountryCodeEnum.PL,
    flag: '🇵🇱',
    displayText: <LocalizedCountry countryIsoCode={CountryCodeEnum.PL} />,
  },
  {
    value: CountryCodeEnum.ES,
    flag: '🇪🇸',
    displayText: <LocalizedCountry countryIsoCode={CountryCodeEnum.ES} />,
  },
  {
    value: CountryCodeEnum.FR,
    flag: '🇫🇷',
    displayText: <LocalizedCountry countryIsoCode={CountryCodeEnum.FR} />,
  },
];
